import { AddRounded } from '@mui/icons-material';
import {
  ButtonBase,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Items } from '@prisma/client';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import { formatDollarAmount } from '../../utils/global/helper';
import useItemQuery from '../../hooks/checkouts/useItemQuery';

export const HEIGHT = 56;
export const SPACING = 8;

interface Props {
  item: Items | null;
  index: number;
  totalCount: number;
  onAddItem: (item: Items | null) => void;
  isFocused?: boolean;
}

function CheckoutItemsSearchRow({
  item,
  index,
  totalCount,
  onAddItem,
  isFocused,
}: Props) {
  const [query] = useItemQuery();
  const theme = useTheme();

  const isOnlyItem = totalCount === 1;
  const isLast = index === totalCount - 1;

  const renderAvatar = () => {
    if (!item) {
      return <AddRounded />;
    }

    const shortenedName = item.name.slice(0, 2);
    return (
      <Typography
        color={theme.palette.medGrey.main}
        variant="body2"
        fontWeight="600">
        {shortenedName}
      </Typography>
    );
  };

  const priceString = item ? formatDollarAmount(item.price || 0) : '';

  return (
    <ButtonBase
      type="button"
      onMouseDown={e => {
        e.preventDefault();
      }}
      onClick={() => {
        onAddItem(item);
      }}
      sx={{
        fontFamily: theme.typography.fontFamily,
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1,
        mb: !isLast && !isOnlyItem ? `${SPACING}px` : 0,
        height: HEIGHT,
        borderRadius: '10px',
        bgcolor: isFocused ? theme.palette.accent.main : 'white',
        boxSizing: 'border-box',
        ':hover': {
          bgcolor: theme.palette.accent.main,
        },
      }}>
      <Stack direction="row" gap={1} alignItems="center">
        <Stack
          alignItems="center"
          justifyContent="center"
          bgcolor={theme.palette.lightGrey.main}
          borderRadius="10px"
          width={40}
          height={40}
          p={1}
          boxSizing="border-box"
          sx={{
            aspectRatio: '1',
          }}>
          {renderAvatar()}
        </Stack>

        <Typography variant="body2" fontWeight="600" noWrap>
          {!item
            ? Number(query)
              ? `Add $${Number(query).toFixed(2)} custom amount`
              : `Add custom item: "${query}"`
            : item.name || 'Unknown item'}
        </Typography>
      </Stack>

      <Collapse
        in={isFocused}
        timeout={theme.transitions.duration.short}
        orientation="horizontal"
        collapsedSize={priceString.length * 8}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ flexShrink: 0, flexGrow: 1 }}>
          {!!item && (
            <Typography variant="body2" color={theme.palette.medGrey.main}>
              {formatDollarAmount(item.price || 0)}
            </Typography>
          )}

          <KeyboardReturnRoundedIcon
            sx={{
              width: isFocused ? 'auto' : 0,
            }}
            htmlColor={theme.palette.medGrey.main}
          />
        </Stack>
      </Collapse>
    </ButtonBase>
  );
}

export default CheckoutItemsSearchRow;
